// ═══════════════════════════════════════════════════════════════
// ArchiveDialog.tsx – archiválás megerősítése a jogszabályi megőrzési idő kijelzésével
//
// Archiválás előtt a szerver kiszámolja a tételek megőrzési idejét (a bejegyzés „Megőrzés” mezője,
// ennek hiányában a 2026-os lista azonos pontja szerint). A párbeszédablak mutatja a leghosszabbat,
// a megőrzési csoportokat, és a megőrzés meghosszabbítható (rövidíteni nem lehet).
// ═══════════════════════════════════════════════════════════════
import React, { useEffect, useState } from 'react';
import { ApiClient, type ArchiveKind, type ArchivePreview } from '../../api/client';
import { X, Archive, RefreshCw, AlertTriangle, ShieldCheck, Info } from 'lucide-react';

export const fmtDay = (s?: string | null) => {
  if (!s) return '';
  const d = new Date(`${s.slice(0, 10)}T00:00:00Z`);
  // „2031. október 21” – a záró pont nélkül, hogy a „-ig” rag helyesen kapcsolódjon
  return isNaN(+d) ? s : d.toLocaleDateString('hu-HU', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' }).replace(/\.$/, '');
};

/** „5 év (a közzétételtől)”, „1+ év”, „nincs megőrzési kötelezettség” */
export const retentionLabel = (r: { years: number | null; min?: boolean | number; basis?: string | null }) => {
  if (r.years === null || r.years === undefined) return 'nincs megadva';
  if (r.years === 0) return 'nincs megőrzési kötelezettség';
  return `${r.years}${r.min ? '+' : ''} év${r.basis === 'publish' ? ' (a közzétételtől)' : ' (az archiválástól)'}`;
};

const KIND_LABEL: Record<ArchiveKind, string> = { record: 'bejegyzés', page: 'oldal', attachment: 'melléklet' };

export const ArchiveDialog: React.FC<{
  isOpen: boolean;
  kind: ArchiveKind;
  ids: number[];
  label?: string;
  onClose: () => void;
  onDone?: (archived: number) => void;
}> = ({ isOpen, kind, ids, label, onClose, onDone }) => {
  const [preview, setPreview] = useState<ArchivePreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [years, setYears] = useState('');
  const [note, setNote] = useState('');

  const load = async (y: string) => {
    setLoading(true); setError(null);
    const r = await ApiClient.previewArchive(kind, ids, y ? Number(y) : null);
    if (r.ok && r.data) setPreview(r.data); else setError(r.error || 'A megőrzési idő nem számítható ki');
    setLoading(false);
  };
  useEffect(() => { if (isOpen) { setYears(''); setNote(''); setPreview(null); load(''); } }, [isOpen, kind, ids.join(',')]);
  useEffect(() => { if (!isOpen) return; const t = setTimeout(() => load(years), 400); return () => clearTimeout(t); }, [years]);

  if (!isOpen) return null;

  const max = preview?.max || null;
  const anyMin = preview?.groups.some((g) => g.min && g.years);
  const submit = async () => {
    setBusy(true); setError(null);
    const r = await ApiClient.archiveItems(kind, ids, { years: years ? Number(years) : null, note: note.trim() || undefined });
    setBusy(false);
    if (r.ok) { onDone?.(r.archived ?? 0); onClose(); } else setError(r.error || 'Az archiválás nem sikerült');
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-xs" role="dialog" aria-modal="true" aria-labelledby="archive-dlg-title"
      onKeyDown={(e) => { if (e.key === 'Escape' && !busy) { e.stopPropagation(); onClose(); } }}
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-2xl shadow-2xl max-h-[92vh] flex flex-col">
        <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-800 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 id="archive-dlg-title" className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2"><Archive className="w-5 h-5 text-amber-600" /> Archiválás</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 break-words">
              {label ? <>„{label}” </> : null}({ids.length > 1 ? `${ids.length} ${KIND_LABEL[kind]}` : KIND_LABEL[kind]}) – {kind === 'page' ? 'az oldal a teljes tartalmával' : kind === 'attachment' ? 'a melléklet (a bejegyzés megőrzési ideje szerint)' : 'a tétel a mellékleteivel együtt'} kikerül a közzétételi felületről és az Archívumba kerül. Az Archívum belső tároló, kérésre tételenként közzétehető.
            </p>
          </div>
          <button type="button" onClick={onClose} disabled={busy} aria-label="Bezárás" className="p-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-auto p-5 space-y-4">
          {loading && !preview && <p className="text-sm text-slate-500 flex items-center gap-2"><RefreshCw className="w-4 h-4 animate-spin" /> A megőrzési idő számítása…</p>}
          {preview && (
            <>
              <div className={`rounded-xl border p-4 ${max ? 'border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30' : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40'}`}>
                <div className="text-[11px] uppercase tracking-wider font-bold text-amber-800 dark:text-amber-300 flex items-center gap-1.5"><ShieldCheck className="w-4 h-4" /> Jogszabály szerinti kötelező megőrzés</div>
                {max ? (
                  <>
                    <div className="mt-1 text-xl font-extrabold text-slate-900 dark:text-white">
                      {fmtDay(max.retain_until)}-ig <span className="text-sm font-semibold text-slate-600 dark:text-slate-300">· {retentionLabel(max)}</span>
                    </div>
                    <div className="text-xs text-slate-600 dark:text-slate-300 mt-1 break-words">
                      {max.pont ? <b>{max.pont} pont</b> : null}{max.pont ? ' – ' : ''}„{max.text || 'egyedi'}”
                    </div>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-2">Addig a tételt csak superadmin törölheti az Archívumból, írásos indoklással (a művelet naplózódik).</p>
                  </>
                ) : (
                  <p className="mt-1 text-sm text-slate-700 dark:text-slate-300">
                    {preview.count === 0 && kind === 'record' ? 'Nincs archiválható tétel.' : 'A tétel(ek)re nincs kötelező megőrzési idő előírva – az Archívumból bármikor törölhető(k).'}
                  </p>
                )}
              </div>

              {preview.groups.length > 1 || (preview.groups[0] && preview.groups[0].count > 1) ? (
                <div className="rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400">
                      <tr><th className="text-left px-3 py-2 font-semibold">Megőrzés (jogszabály)</th><th className="text-right px-3 py-2 font-semibold">Tétel</th><th className="text-right px-3 py-2 font-semibold">Legkésőbb</th></tr>
                    </thead>
                    <tbody>
                      {preview.groups.map((g, i) => (
                        <tr key={i} className="border-t border-slate-100 dark:border-slate-800">
                          <td className="px-3 py-2 text-slate-700 dark:text-slate-300">
                            <div className="font-semibold">{retentionLabel(g)}</div>
                            <div className="text-[11px] text-slate-500 break-words">{g.text || 'nincs megadva'}{g.ponts.length ? ` · ${g.ponts.slice(0, 6).join(', ')}${g.ponts.length > 6 ? '…' : ''}` : ''}</div>
                          </td>
                          <td className="px-3 py-2 text-right text-slate-700 dark:text-slate-300">{g.count}</td>
                          <td className="px-3 py-2 text-right whitespace-nowrap text-slate-700 dark:text-slate-300">{g.max_until ? fmtDay(g.max_until) : '–'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}

              {anyMin && (
                <p className="text-xs text-amber-800 dark:text-amber-300 flex items-start gap-1.5"><AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
                  „Külön jogszabályban meghatározott ideig, de legalább …” – a kijelzett idő a minimum. Ha a vonatkozó jogszabály hosszabbat ír elő, add meg lent.</p>
              )}
              {preview.unknown > 0 && (
                <p className="text-xs text-slate-600 dark:text-slate-400 flex items-start gap-1.5"><Info className="w-4 h-4 shrink-0 mt-px" />
                  {preview.unknown} tételnél nincs megadva megőrzési idő (sem a bejegyzésben, sem a sablonban). Szükség esetén add meg lent.</p>
              )}

              <div className="grid sm:grid-cols-[12rem_1fr] gap-3 items-start">
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 pt-2" htmlFor="archive-years">Hosszabb megőrzés (év, nem kötelező)</label>
                <div>
                  <input id="archive-years" type="number" min={1} max={100} inputMode="numeric" value={years} onChange={(e) => setYears(e.target.value.replace(/[^\d]/g, '').slice(0, 3))}
                    placeholder="pl. 8" className="w-28 px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl text-sm text-slate-900 dark:text-white" />
                  <p className="text-[11px] text-slate-500 mt-1">Csak meghosszabbítani lehet: ha a jogszabályi idő hosszabb, az marad érvényben.</p>
                </div>
                <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 pt-2" htmlFor="archive-note">Megjegyzés</label>
                <textarea id="archive-note" rows={2} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} placeholder="pl. ügyiratszám, az archiválás oka"
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl text-sm text-slate-900 dark:text-white" />
              </div>
            </>
          )}
          {error && <p role="alert" className="text-xs text-rose-700 dark:text-rose-300 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" />{error}</p>}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-800 flex justify-end gap-2">
          <button type="button" onClick={onClose} disabled={busy} className="px-4 py-2 rounded-xl text-sm font-semibold border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer">Mégse</button>
          <button type="button" onClick={submit} disabled={busy || loading || !preview || (kind === 'record' && preview.count === 0)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-amber-600 hover:bg-amber-500 text-white cursor-pointer disabled:opacity-50">
            {busy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Archive className="w-4 h-4" />} Archiválás
          </button>
        </div>
      </div>
    </div>
  );
};

export default ArchiveDialog;
